import React, { ReactNode, createContext, useContext, useId } from 'react'
import { cn } from './cn'
import { Text } from './Text'

interface FieldContextValue {
  id: string
  describedBy?: string
  invalid: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)
export const useFieldContext = () => useContext(FieldContext)

export interface FieldProps {
  label?: ReactNode
  /** Right-aligned extra next to the label (e.g. a "Max" link or balance). */
  labelAside?: ReactNode
  helper?: ReactNode
  /** A message shows below the control; `true` only marks it invalid. */
  error?: ReactNode | boolean
  id?: string
  className?: string
  children: ReactNode
}

/** Label, helper and error around any control. Inputs inside pick up the id and error state. */
export const Field = ({
  label,
  labelAside,
  helper,
  error,
  id,
  className,
  children,
}: FieldProps) => {
  const autoId = useId()
  const fieldId = id ?? `field-${autoId.replace(/:/g, '')}`
  const message = typeof error === 'boolean' ? null : error
  const messageId = message || helper ? `${fieldId}-message` : undefined

  return (
    <FieldContext.Provider value={{ id: fieldId, describedBy: messageId, invalid: !!error }}>
      <div className={cn('flex flex-col gap-2', className)}>
        {(label || labelAside) && (
          <div className="flex items-center justify-between gap-2">
            {label && (
              <Text as="label" htmlFor={fieldId} variant="label" tone="fg-2">
                {label}
              </Text>
            )}
            {labelAside && <div className="ml-auto">{labelAside}</div>}
          </div>
        )}
        {children}
        {(message || helper) && (
          <Text
            id={messageId}
            variant="caption"
            tone={message ? 'danger' : 'fg-3'}
            role={message ? 'alert' : undefined}
          >
            {message || helper}
          </Text>
        )}
      </div>
    </FieldContext.Provider>
  )
}
